import { useEffect, useRef, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { History, Lock, CheckCircle2, GitBranch, Eye, FileText, ImagePlus, Trash2 } from "lucide-react";
import {
  finalizeAssessment, listAssessmentVersions, cloneAssessmentVersion,
  getClientLogo, uploadClientLogo, deleteClientLogo,
} from "../lib/adminApi";
function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium", timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

// Right-hand drawer that lists a single assessment's draft + finalized
// snapshots and exposes View / Create-New-Version actions per row.
// PDF export lives exclusively in the finalized Executive Summary Report view
// — this drawer intentionally has no PDF action.
export function VersionHistoryDrawer({
  open, onOpenChange, assessmentId, refreshKey, onViewVersion, onVersionChanged,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [confirmCloneVid, setConfirmCloneVid] = useState(null);

  useEffect(() => {
    if (!open || !assessmentId) return;
    setLoading(true); setErr("");
    listAssessmentVersions(assessmentId)
      .then(setData)
      .catch(() => setErr("Failed to load versions"))
      .finally(() => setLoading(false));
  }, [open, assessmentId, refreshKey]);

  const handleOpenDraft = () => {
    onOpenChange(false);
    // Clear any version query param to return to the live draft view.
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("version");
      window.history.replaceState({}, "", url.toString());
    }
    onVersionChanged?.();
  };

  const handleClone = async (vid) => {
    try {
      await cloneAssessmentVersion(assessmentId, vid);
      setConfirmCloneVid(null);
      onOpenChange(false);
      onVersionChanged?.();
      // Clear version param, return to editable draft mode
      if (typeof window !== "undefined") {
        const url = new URL(window.location.href);
        url.searchParams.delete("version");
        window.history.replaceState({}, "", url.toString());
      }
    } catch {
      setErr("Failed to create new version");
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-[420px] sm:max-w-[420px] overflow-y-auto"
          data-testid="version-history-drawer"
        >
          <SheetHeader className="border-b border-[#E5E7EB] pb-3 mb-4">
            <SheetTitle
              className="text-[15px] font-semibold text-[#111827] flex items-center gap-2"
              style={{ fontFamily: "Chivo" }}
            >
              <History className="w-4 h-4 text-[#025979]" />
              Version History
            </SheetTitle>
            <SheetDescription className="text-xs text-[#6B7280]">
              Finalized snapshots are immutable. The active draft captures all
              in-progress edits.
            </SheetDescription>
          </SheetHeader>

          <ClientLogoSection assessmentId={assessmentId} refreshKey={refreshKey} />

          {loading && (
            <p className="text-xs text-[#9CA3AF]" data-testid="versions-loading">
              Loading versions…
            </p>
          )}
          {err && (
            <p className="text-xs text-red-600" data-testid="versions-error">{err}</p>
          )}

          {data && (
            <ul className="space-y-3" data-testid="versions-list">
              {data.draft && (
                <li
                  className="border border-[#E5E7EB] rounded-md p-3 bg-[#FCFCFD]"
                  data-testid={`version-row-draft-v${data.draft.version_number}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[13px] font-semibold text-[#111827]">
                      v{data.draft.version_number}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">
                      Draft
                    </span>
                  </div>
                  <p className="text-[11px] text-[#6B7280]">
                    Last updated: {formatDate(data.draft.updated_at)}
                  </p>
                  <div className="mt-2 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-[11px] h-7 px-2 border-[#E5E7EB]"
                      onClick={handleOpenDraft}
                      data-testid="open-draft-btn"
                    >
                      <FileText className="w-3 h-3 mr-1" /> Open Draft
                    </Button>
                  </div>
                </li>
              )}

              {data.versions.map((v) => (
                <li
                  key={v.id}
                  className="border border-[#E5E7EB] rounded-md p-3 bg-white"
                  data-testid={`version-row-v${v.version_number}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[13px] font-semibold text-[#111827]">
                      v{v.version_number}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#E6F2F5] text-[#025979] font-medium inline-flex items-center gap-1">
                      <Lock className="w-2.5 h-2.5" />
                      Finalized
                    </span>
                  </div>
                  <p className="text-[11px] text-[#6B7280]">
                    Finalized on {formatDate(v.created_at)}
                  </p>
                  {v.created_by?.email && (
                    <p className="text-[11px] text-[#9CA3AF]">by {v.created_by.email}</p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-[11px] h-7 px-2 border-[#E5E7EB]"
                      onClick={() => { onOpenChange(false); onViewVersion?.(v.id); }}
                      data-testid={`view-version-btn-${v.id}`}
                    >
                      <Eye className="w-3 h-3 mr-1" /> View
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-[11px] h-7 px-2 border-[#E5E7EB] text-[#025979]"
                      onClick={() => setConfirmCloneVid(v.id)}
                      data-testid={`clone-version-btn-${v.id}`}
                    >
                      <GitBranch className="w-3 h-3 mr-1" /> Create New Version
                    </Button>
                  </div>
                </li>
              ))}

              {!data.draft && data.versions.length === 0 && (
                <li className="text-xs text-[#9CA3AF]">No versions yet.</li>
              )}
            </ul>
          )}
        </SheetContent>
      </Sheet>

      {/* Confirm cloning an older finalized version back onto the live draft */}
      <Dialog open={!!confirmCloneVid} onOpenChange={(v) => !v && setConfirmCloneVid(null)}>
        <DialogContent className="sm:max-w-[440px]" data-testid="clone-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-[15px]" style={{ fontFamily: "Chivo" }}>
              Create a new version from this snapshot?
            </DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280] pt-1 leading-relaxed">
              The selected finalized version will be restored as the new editable
              draft. Any unsaved changes in the current draft will be overwritten.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => setConfirmCloneVid(null)}
              data-testid="clone-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="text-xs bg-[#025979] hover:bg-[#024a63] text-white"
              onClick={() => handleClone(confirmCloneVid)}
              data-testid="clone-confirm-btn"
            >
              Restore & Create Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Confirmation modal shown when a consultant clicks "Finalize Assessment".
export function FinalizeModal({ open, onOpenChange, onConfirm, submitting }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[460px]"
        data-testid="finalize-confirm-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#E6F2F5] text-[#025979]">
              <CheckCircle2 className="w-4 h-4" />
            </span>
            <DialogTitle
              className="text-[15px] font-semibold text-[#111827] tracking-tight"
              style={{ fontFamily: "Chivo" }}
            >
              Finalize Evaluation
            </DialogTitle>
          </div>
          <DialogDescription className="text-xs text-[#6B7280] pt-1 leading-relaxed">
            This will lock the current evaluation and create a client-ready
            version snapshot.
            <br /><br />
            You will still be able to create a new version later if changes
            are needed.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="finalize-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="text-xs bg-[#025979] hover:bg-[#024a63] text-white"
            onClick={onConfirm}
            disabled={submitting}
            data-testid="finalize-confirm-btn"
          >
            {submitting ? "Finalizing…" : "Finalize & Create Version"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Small banner shown at top of the assessment page when viewing a frozen
// snapshot (read-only) OR when the live draft is locked because it has been
// finalized.
export function VersionBanner({
  mode, versionNumber, finalizedAt, finalizedBy, onExit, onCreateNewVersion, isRfp = false,
}) {
  if (mode === "readonly") {
    return (
      <div
        data-testid="version-readonly-banner"
        className="mb-4 bg-[#FFF7ED] border border-[#FDBA74] rounded-md px-3 py-2 flex flex-wrap items-center justify-between gap-2"
      >
        <div className="flex items-start gap-2">
          <Lock className="w-3.5 h-3.5 mt-0.5 text-[#C2410C]" />
          <div className="text-[11px] leading-snug">
            <span className="font-semibold text-[#9A3412]">
              Viewing Finalized Version v{versionNumber} (Read-only)
            </span>
            <div className="text-[#C2410C]">
              Finalized {formatDate(finalizedAt)}
              {finalizedBy?.email ? ` · by ${finalizedBy.email}` : ""}
            </div>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="text-[11px] h-7 px-2 border-[#FDBA74] text-[#9A3412] bg-white"
          onClick={onExit}
          data-testid="exit-version-view-btn"
        >
          Return to Draft
        </Button>
      </div>
    );
  }
  if (mode === "locked") {
    return (
      <div
        data-testid="version-locked-banner"
        className="mb-4 bg-[#F0F9FF] border border-[#0EA5E9]/30 rounded-md px-3 py-2 flex flex-wrap items-center justify-between gap-2"
      >
        <div className="flex items-start gap-2">
          <Lock className="w-3.5 h-3.5 mt-0.5 text-[#025979]" />
          <div className="text-[11px] leading-snug">
            <span className="font-semibold text-[#025979]">
              {isRfp ? "Bid" : "Evaluation"} finalized (v{versionNumber}) — Read-only
            </span>
            <div className="text-[#0369A1]">
              Finalized {formatDate(finalizedAt)}
              {finalizedBy?.email ? ` · by ${finalizedBy.email}` : ""}. Create a
              new version to resume {isRfp ? "bid " : ""}editing.
            </div>
          </div>
        </div>
        {onCreateNewVersion && (
          <Button
            size="sm"
            className="text-[11px] h-7 px-2 bg-[#025979] hover:bg-[#024a63] text-white"
            onClick={onCreateNewVersion}
            data-testid="create-new-version-btn"
          >
            <GitBranch className="w-3 h-3 mr-1" />
            Create New Version
          </Button>
        )}
      </div>
    );
  }
  return null;
}


// Small "Client Logo" widget embedded in the Version History drawer.
// Allows uploading / removing a per-assessment logo that surfaces in PDFs.
function ClientLogoSection({ assessmentId, refreshKey }) {
  const [logo, setLogo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    if (!assessmentId) return;
    getClientLogo(assessmentId).then(setLogo).catch(() => setLogo(null));
  };
  useEffect(load, [assessmentId, refreshKey]);

  const onPick = () => fileRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/image\/(png|jpe?g)/i.test(file.type)) {
      setErr("Only PNG or JPG files are supported");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setErr("Logo must be under 2 MB");
      return;
    }
    setErr("");
    setBusy(true);
    try {
      await uploadClientLogo(assessmentId, file);
      load();
    } catch {
      setErr("Upload failed");
    } finally {
      setBusy(false);
    }
  };
  const onDelete = async () => {
    setBusy(true);
    try {
      await deleteClientLogo(assessmentId);
      load();
    } catch {
      setErr("Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mb-4 border border-[#E5E7EB] rounded-md p-3 bg-[#FCFCFD]"
      data-testid="client-logo-section"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-semibold text-[#374151] uppercase tracking-wider">
          Client Logo
        </span>
        <span className="text-[10px] text-[#9CA3AF]">PNG / JPG · ≤ 2 MB</span>
      </div>
      <div className="flex items-center gap-3">
        <div
          className="w-16 h-10 rounded-sm border border-[#E5E7EB] bg-white flex items-center justify-center overflow-hidden"
          data-testid="client-logo-preview"
        >
          {logo?.has_logo ? (
            <img loading="lazy"
              src={logo.data_url}
              alt="Client logo"
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <span className="text-[9px] text-[#9CA3AF]">No logo</span>
          )}
        </div>
        <div className="flex-1 flex flex-wrap gap-1.5">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={onFile}
            data-testid="client-logo-input"
          />
          <Button
            size="sm"
            variant="outline"
            className="text-[11px] h-7 px-2 border-[#E5E7EB]"
            onClick={onPick}
            disabled={busy}
            data-testid="client-logo-upload-btn"
          >
            <ImagePlus className="w-3 h-3 mr-1" />
            {logo?.has_logo ? "Replace" : "Upload"}
          </Button>
          {logo?.has_logo && (
            <Button
              size="sm"
              variant="outline"
              className="text-[11px] h-7 px-2 border-[#E5E7EB] text-red-600"
              onClick={onDelete}
              disabled={busy}
              data-testid="client-logo-delete-btn"
            >
              <Trash2 className="w-3 h-3 mr-1" /> Remove
            </Button>
          )}
        </div>
      </div>
      {err && (
        <p className="mt-2 text-[10px] text-red-600" data-testid="client-logo-error">{err}</p>
      )}
    </div>
  );
}
